<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Stack, SelectCard, Text } from '@Shelly/shelly-design-system';
	import type { FlowNode } from '../../types/flow-types';
	import BaseInspector from '../BaseInspector.svelte';
	import { flowEditorState } from '../../../../stores/flow_editor';

	export let node: FlowNode;

	const dispatch = createEventDispatcher<{
		update: FlowNode;
	}>();

	// Resolve execution mode from node data
	$: executionMode = resolveExecutionMode(node.data);
	$: holdOff = Number(node.data.hold_off ?? 5);
	$: isOnce = executionMode === 'once';

	function resolveExecutionMode(d: any): string {
		if (d.executionMode) return d.executionMode;
		const ctId = (d.cond_type as any)?.id;
		if (ctId === 1 || ctId === 'once') return 'once';
		if (ctId === 2 || ctId === 'any') return 'any';
		if (ctId === 3 || ctId === 'repeat') return 'repeat';
		return 'once';
	}

	const triggerTypes = [
		{
			value: 'any',
			title: 'On any change',
			description:
				'Triggers the scene every time when a monitored value changes and meets the pre-set conditions.',
		},
		{
			value: 'repeat',
			title: 'Repeatedly',
			description:
				'Checks the condition every minute and triggers actions if the condition state remains unchanged.',
		},
		{
			value: 'once',
			title: 'Once',
			description:
				'Activates the scene once when the condition is met and requires the condition to be repeated for reactivation.',
		},
	];

	function setExecutionMode(mode: string) {
		flowEditorState.updateNode(node.id, {
			data: { ...node.data, executionMode: mode },
		});
	}

	function setHoldOff(value: number) {
		const clamped = Math.max(0, Math.round(value));
		flowEditorState.updateNode(node.id, {
			data: { ...node.data, hold_off: clamped },
		});
	}

	function handleHoldOffInput(e: Event) {
		const target = e.target as HTMLInputElement;
		const val = parseInt(target.value, 10);
		if (!isNaN(val)) {
			setHoldOff(val);
		}
	}
</script>

<BaseInspector
	{node}
	title={$format('flow_trigger_settings', { default: 'Trigger Settings' })}
	on:update
>
	<Stack direction="row" gap="m">
		<Text variant="body" size="s" class="section-label">
			{$format('condition_type_title', { default: 'Condition type' })}
		</Text>

		<div class="trigger-options">
			{#each triggerTypes as trigger}
				<SelectCard
					variant="radio"
					type="horizontal"
					class="width-full"
					selected={executionMode === trigger.value}
					on:click={() => setExecutionMode(trigger.value)}
					title={trigger.title}
					descriptiveText={trigger.description}
					size="m"
				/>
			{/each}
		</div>

		{#if isOnce}
			<div class="holdoff-section">
				<Text variant="body" size="s">
					{$format('condition_type_valid_minutes', {
						values: { min: holdOff },
						default: `Condition is valid ${holdOff} minutes`,
					})}
				</Text>

				<div class="holdoff-stepper">
					<button
						class="stepper-btn"
						disabled={holdOff <= 0}
						on:click={() => setHoldOff(holdOff - 1)}
						aria-label={$format('button_decrease_value', { default: 'Decrease value' })}
					>
						<i class="fas fa-minus" />
					</button>
					<div class="stepper-input-wrap">
						<i class="fas fa-clock stepper-icon" />
						<input
							type="number"
							class="stepper-input"
							value={holdOff}
							min="0"
							on:input={handleHoldOffInput}
							aria-label={$format('input_holdoff_minutes', {
								default: 'Hold-off minutes',
							})}
						/>
					</div>
					<button
						class="stepper-btn"
						on:click={() => setHoldOff(holdOff + 1)}
						aria-label={$format('button_increase_value', { default: 'Increase value' })}
					>
						<i class="fas fa-plus" />
					</button>
				</div>
			</div>
		{/if}
	</Stack>
</BaseInspector>

<style>
	:global(.section-label) {
		color: #b0b0b0 !important;
	}

	.trigger-options {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.holdoff-section {
		display: flex;
		flex-direction: column;
		gap: 8px;
		margin-top: 4px;
		padding-top: 12px;
		border-top: 1px solid rgba(255, 255, 255, 0.08);
	}

	.holdoff-stepper {
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.stepper-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 36px;
		height: 36px;
		border-radius: 8px;
		border: 1px solid rgba(255, 255, 255, 0.15);
		background: rgba(255, 255, 255, 0.05);
		color: #fff;
		cursor: pointer;
		font-size: 0.8rem;
		transition: all 0.15s ease;
	}

	.stepper-btn:hover:not(:disabled) {
		background: rgba(59, 130, 246, 0.2);
		border-color: rgba(59, 130, 246, 0.4);
	}

	.stepper-btn:disabled {
		opacity: 0.35;
		cursor: not-allowed;
	}

	.stepper-input-wrap {
		flex: 1;
		display: flex;
		align-items: center;
		gap: 6px;
		background: rgba(255, 255, 255, 0.05);
		border: 1px solid rgba(255, 255, 255, 0.15);
		border-radius: 8px;
		padding: 0 10px;
		height: 36px;
	}

	.stepper-icon {
		color: #9ca3af;
		font-size: 0.85rem;
	}

	.stepper-input {
		background: transparent;
		border: none;
		color: #fff;
		font-size: 0.9rem;
		width: 100%;
		text-align: center;
		outline: none;
		appearance: textfield;
		-moz-appearance: textfield;
	}

	.stepper-input::-webkit-outer-spin-button,
	.stepper-input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
</style>
